import { FC, useEffect, useRef } from "react";

import { MarkdownMessage } from "@ryde/controls";
import { AIRoleTypeEnum, IChatHistoryItem } from "@ryde/shared";

import { AiAdvisorChatLoader } from "./ai-advisor-chat-loader";
import { AiChatElements } from "./ai-chat-elements";
import { DetailsLinkStyled } from "./bulk-ai-advisor-drawer.styled";

interface IChatRendererProps {
  chatHistory: IChatHistoryItem[];
  isLoading: boolean;
  isStreaming?: boolean;
  detailsLink?: {
    onClick: (messageIndex?: number) => void;
    label: string;
  };
  /** Send a tapped option as the user's next message. Omit to render controls disabled. */
  onSendElementValue?: (value: string) => void;
}

export const AiAdvisorChatRenderer: FC<IChatRendererProps> = ({
  chatHistory,
  isLoading,
  isStreaming = false,
  detailsLink,
  onSendElementValue,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const isLastAssistantMessage = (index: number): boolean =>
    index === chatHistory.length - 1 &&
    chatHistory[index]?.role === AIRoleTypeEnum.Assistant;

  const shouldShowDetailsLink = (index: number): boolean =>
    (!!detailsLink && !isLastAssistantMessage(index)) ||
    (!!detailsLink && !isStreaming);

  return (
    <div className="inner-conversation-wrapper" ref={containerRef}>
      {chatHistory?.map(({ role, text, messageIndex, elements }, index) =>
        role === AIRoleTypeEnum.User ? (
          <span key={index} className="user-message">
            {text}
          </span>
        ) : (
          <div className="assistant-message-block" key={index}>
            {text ? (
              <MarkdownMessage
                text={text}
                allowOnlyInnerLinks
                elements={elements}
              />
            ) : null}
            {isStreaming && isLastAssistantMessage(index) && (
              <span className="streaming-cursor" aria-hidden="true" />
            )}
            <AiChatElements
              elements={elements}
              onSend={onSendElementValue ?? (() => undefined)}
              // Older turns stay visible so the transcript reads correctly, but answering
              // a question from six messages back would confirm something no longer
              // pending.
              interactive={
                !!onSendElementValue &&
                !isStreaming &&
                isLastAssistantMessage(index)
              }
            />
            {shouldShowDetailsLink(index) && (
              <DetailsLinkStyled
                onClick={() => detailsLink?.onClick(messageIndex)}
                role="button"
              >
                {detailsLink?.label}
                <span className="details-link-arrow" aria-hidden>
                  ››
                </span>
              </DetailsLinkStyled>
            )}
          </div>
        ),
      )}

      {isLoading && !isStreaming && <AiAdvisorChatLoader />}
    </div>
  );
};
